import React, { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import Panel from "../components/Panel";
import {
    Bot, RefreshCcw, Send, Calculator, PackageSearch, Percent,
    AlertTriangle, CheckCircle2, ChevronRight, Sparkles,
    TrendingUp, Newspaper, HardHat, Users, ShieldCheck, Rocket, MessageCircle, FileText
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Calculator, PackageSearch, Percent, TrendingUp, Newspaper, HardHat, Users, ShieldCheck, Rocket };

const SEVERITY_CLASS = {
    High: "text-[var(--neg)] border-[var(--neg)]/40 bg-[var(--neg)]/5",
    Medium: "text-[var(--brand-amber)] border-[var(--brand-amber)]/40 bg-[var(--brand-amber-dim)]",
    Low: "text-[var(--text-primary)]/70 border-panel bg-[var(--bg-base)]",
};

const fmt = (v) => {
    if (v === null || v === undefined || v === 0) return "—";
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${v.toLocaleString("en-IN")}`;
};

const renderMD = (content) => content.split("\n").map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    const cleaned = line.replace(/^#{1,3}\s/, "").replace(/\*\*(.+?)\*\*/g, "$1");
    const isHeader = /^#{1,3}\s/.test(line);
    const cls = isHeader
        ? "font-display font-bold text-[var(--brand-amber)] text-sm mt-2"
        : "text-[var(--text-primary)]/90";
    return <div key={i} className={`${cls} leading-relaxed`}>{cleaned}</div>;
});

function AgentCard({ agent, digest, loading, onOpen }) {
    const Icon = ICONS[agent.icon] || Bot;
    const signalCount = digest?.signals?.length || 0;
    const highCount = digest?.signals?.filter((s) => s.severity === "High").length || 0;

    return (
        <button
            data-testid={`agent-card-${agent.key}`}
            onClick={onOpen}
            className="text-left bg-panel border border-panel hover:border-[var(--brand-amber)] transition-colors p-5 group"
        >
            <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 flex items-center justify-center border border-panel bg-[var(--bg-base)]"
                     style={{ color: agent.color }}>
                    <Icon size={20} />
                </div>
                <ChevronRight className="text-[var(--text-secondary)] group-hover:text-[var(--brand-amber)]" size={18} />
            </div>
            <div className="font-display font-bold text-[var(--text-primary)] text-base">{agent.title}</div>
            <div className="text-[11px] font-body text-[var(--text-secondary)] mt-1 leading-snug">
                {agent.description}
            </div>
            <div className="mt-4 flex items-center gap-3 text-[10px] font-mono-fin uppercase tracking-wider">
                {loading ? (
                    <span className="text-[var(--text-secondary)]">Loading…</span>
                ) : (
                    <>
                        <span className={`px-1.5 py-0.5 border ${highCount > 0 ? "text-[var(--neg)] border-[var(--neg)]/40" : "text-[var(--pos)] border-[var(--pos)]/40"}`}>
                            {highCount} High
                        </span>
                        <span className="text-[var(--text-secondary)]">{signalCount} Signals</span>
                    </>
                )}
            </div>
        </button>
    );
}

function AgentPanel({ agent, onBack }) {
    const [digest, setDigest] = useState(null);
    const [approvals, setApprovals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const bottomRef = useRef(null);
    const Icon = ICONS[agent.icon] || Bot;

    const refresh = async () => {
        setLoading(true);
        setError(null);
        try {
            const [d, a] = await Promise.all([
                api.get(`/agents/${agent.key}/digest`),
                api.get(`/agents/${agent.key}/approvals`),
            ]);
            setDigest(d.data);
            setApprovals(a.data.approvals || []);
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Unknown error";
            setError(msg);
            // Try approvals-only fallback (cheap, no LLM)
            try {
                const a = await api.get(`/agents/${agent.key}/approvals`);
                setApprovals(a.data.approvals || []);
            } catch (e2) { /* ignore secondary error */ }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, [agent.key]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const ask = async (text) => {
        if (!text.trim() || busy) return;
        const user = { role: "user", content: text };
        const assistant = { role: "assistant", content: "" };
        setMessages((m) => [...m, user, assistant]);
        setInput("");
        setBusy(true);
        try {
            const res = await fetch(`${API}/agents/${agent.key}/ask/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: text, session_id: sessionId }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;
                    try {
                        const evt = JSON.parse(line.slice(5).trim());
                        if (evt.type === "session") setSessionId(evt.session_id);
                        else if (evt.type === "delta") {
                            setMessages((m) => {
                                const c = [...m];
                                c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + evt.content };
                                return c;
                            });
                        } else if (evt.type === "error") {
                            setMessages((m) => {
                                const c = [...m];
                                c[c.length - 1] = { ...c[c.length - 1], content: `⚠️ ${evt.content}` };
                                return c;
                            });
                        }
                    } catch (e) { /* ignore malformed SSE line */ }
                }
            }
        } catch (e) {
            setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], content: `⚠️ Connection error: ${e.message}` };
                return c;
            });
        } finally {
            setBusy(false);
        }
    };

    const suggestions = {
        cfo_briefing: [
            "Give me my morning briefing in 5 bullet points.",
            "What is the single biggest ₹ concern right now?",
            "What bold move should I make this week?",
        ],
        bookkeeper: [
            "What is my highest-risk unpaid receivable right now?",
            "Show me all overdue vendor bills grouped by category.",
            "Are there duplicate invoices I should flag?",
        ],
        cashflow: [
            "Do I have a cash shortfall in the next 90 days?",
            "How much CC headroom do I have left?",
            "Which 3 collections would improve my runway most?",
        ],
        procurement: [
            "Which materials must I reorder this week and from which vendor?",
            "How much money is stuck in dead stock?",
            "Which strategic vendor has the biggest unpaid dues right now?",
        ],
        sales: [
            "Which tender should I prioritise winning this quarter?",
            "Which client should I call TODAY to unlock cash?",
            "Where should I add tenders to reach ₹30 Cr weighted pipeline?",
        ],
        project_manager: [
            "Which project is at highest cost-overrun risk?",
            "Show me all projects where SPI is below 0.95.",
            "Where can I accelerate billing this month?",
        ],
        hr_payroll: [
            "What statutory filings are due in the next 15 days?",
            "Is my headcount cost too high vs turnover?",
            "Which employees are at attrition risk?",
        ],
        gst: [
            "What is my provisional GSTR-3B liability for this month?",
            "Which vendors have not uploaded their GSTR-1 yet?",
            "What is my ITC-at-risk exposure and how do I recover it?",
        ],
        auditor: [
            "Show me the top 3 anomalies with ₹ impact.",
            "Are there duplicate invoices I need to reverse?",
            "What management letter items should I raise this quarter?",
        ],
        growth: [
            "How do I get from ₹15 Cr to ₹100 Cr in 18 months?",
            "Which 3 tenders give the best weighted profit?",
            "What is the single highest-ROI initiative right now?",
        ],
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <button
                    data-testid="agent-back-btn"
                    onClick={onBack}
                    className="text-[11px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] hover:text-[var(--brand-amber)] transition-colors border border-panel px-3 py-1.5"
                >
                    ← All Agents
                </button>
                <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 flex items-center justify-center border border-panel bg-[var(--bg-base)]" style={{ color: agent.color }}>
                        <Icon size={18} />
                    </div>
                    <div>
                        <div className="font-display font-black text-xl leading-none">{agent.title}</div>
                        <div className="font-mono-fin text-[10px] uppercase tracking-wider text-[var(--text-secondary)] mt-1">{agent.description}</div>
                    </div>
                </div>
                <button
                    data-testid="agent-refresh-btn"
                    onClick={refresh}
                    disabled={loading}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                    <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
                    {loading ? "Analyzing…" : "Refresh"}
                </button>
                <button
                    data-testid="agent-telegram-btn"
                    onClick={async () => {
                        try {
                            const r = await api.post(`/notify/telegram/digest/${agent.key}`, {});
                            if (r.data.sent > 0) {
                                toast.success(`Sent to ${r.data.sent} chat(s) via Telegram`);
                            } else if (r.data.ok) {
                                toast.success("Sent to Telegram");
                            } else {
                                toast.info("No chats registered — set up in Notifications page");
                            }
                        } catch (e) {
                            toast.error(e.response?.data?.detail || "Telegram send failed");
                        }
                    }}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5"
                    title="Push this digest to Telegram"
                >
                    <MessageCircle size={12} /> Telegram
                </button>
                <button
                    data-testid="agent-pdf-btn"
                    onClick={async () => {
                        const t = toast.loading("Generating PDF board-pack…");
                        try {
                            const r = await api.post(`/notify/telegram/pdf/${agent.key}`, {});
                            toast.dismiss(t);
                            if (r.data.sent > 0 || r.data.ok) {
                                toast.success(`PDF sent · ${r.data.file}`);
                            } else {
                                toast.info("No chats registered");
                            }
                        } catch (e) {
                            toast.dismiss(t);
                            toast.error(e.response?.data?.detail || "PDF send failed");
                        }
                    }}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5"
                    title="Generate PDF board-pack and send to Telegram"
                >
                    <FileText size={12} /> PDF → TG
                </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
                <div className="xl:col-span-3 space-y-3">
                    <Panel testId="agent-digest-panel" title="Daily Digest" subtitle={digest ? `As of ${new Date(digest.as_of).toLocaleString("en-IN")}` : (error ? "Unavailable" : "Loading…")}>
                        {loading && <div className="text-xs text-[var(--text-secondary)] font-mono-fin py-4">Running snapshot + LLM…</div>}
                        {!loading && error && (
                            <div className="border border-[var(--neg)]/40 bg-[var(--neg)]/5 p-3">
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--neg)] mb-1">// Digest Unavailable</div>
                                <div className="text-xs text-[var(--text-primary)]/85">{error}</div>
                                <div className="text-[11px] text-[var(--text-secondary)] mt-2">Try again in a moment — rule signals below are still fresh.</div>
                            </div>
                        )}
                        {!loading && !error && digest && (
                            <div className="text-sm font-body space-y-1">
                                {renderMD(digest.narrative || "(No narrative)")}
                            </div>
                        )}
                    </Panel>

                    <Panel testId="agent-approvals-panel" title={`Pending Approvals · ${approvals.length}`} subtitle="Action items requiring your decision">
                        {approvals.length === 0 && !loading && (
                            <div className="flex items-center gap-2 text-[var(--pos)] text-xs font-mono-fin">
                                <CheckCircle2 size={14} /> All clear — no pending flags.
                            </div>
                        )}
                        <div className="space-y-2">
                            {approvals.map((a) => (
                                <div key={a.id} data-testid={`approval-${a.id}`} className={`border p-3 ${SEVERITY_CLASS[a.severity] || SEVERITY_CLASS.Low}`}>
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <AlertTriangle size={12} />
                                                <span className="text-[10px] font-mono-fin uppercase tracking-wider">{a.severity} · {a.id}</span>
                                            </div>
                                            <div className="font-display font-bold text-[var(--text-primary)] text-sm">{a.title}</div>
                                            <div className="text-xs text-[var(--text-primary)]/85 mt-1">{a.detail}</div>
                                            <div className="text-[11px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] mt-2">→ {a.recommended_action}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono-fin text-[9px] uppercase tracking-wider text-[var(--text-secondary)]">Amount</div>
                                            <div className="font-mono-fin text-sm text-[var(--text-primary)]">{fmt(a.amount)}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Panel>
                </div>

                <div className="xl:col-span-2 bg-panel border border-panel flex flex-col" style={{ minHeight: "60vh" }}>
                    <div className="px-4 py-3 border-b border-panel flex items-center justify-between">
                        <div className="text-xs uppercase tracking-[0.15em] font-mono-fin text-[var(--text-primary)] flex items-center gap-2">
                            <Sparkles size={12} className="text-[var(--brand-amber)]" /> Ask the {agent.title.replace(" Agent", "")}
                        </div>
                        <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-[var(--pos)] rounded-full blink"></span> Live
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                        {messages.length === 0 && (
                            <div className="space-y-2">
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Suggested prompts</div>
                                {(suggestions[agent.key] || []).map((s, i) => (
                                    <button
                                        key={i}
                                        data-testid={`agent-suggest-${i}`}
                                        onClick={() => ask(s)}
                                        disabled={busy}
                                        className="w-full text-left border border-panel p-2.5 hover:border-[var(--brand-amber)] transition-colors text-xs text-[var(--text-primary)]/85 disabled:opacity-50"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[92%] p-3 border ${m.role === "user" ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel bg-[var(--bg-base)]"}`}>
                                    <div className="text-[9px] font-mono-fin uppercase tracking-[0.15em] mb-2 opacity-70">
                                        {m.role === "user" ? "// You" : `// ${agent.title}`}
                                    </div>
                                    <div className="text-xs font-body whitespace-pre-wrap">
                                        {m.role === "assistant" ? renderMD(m.content || "▊") : m.content}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={bottomRef} />
                    </div>

                    <div className="border-t border-panel p-2.5 flex gap-2">
                        <input
                            data-testid="agent-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && ask(input)}
                            disabled={busy}
                            placeholder={`Ask ${agent.title}…`}
                            className="flex-1 bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-body focus:border-[var(--brand-amber)] focus:outline-none disabled:opacity-50"
                        />
                        <button
                            data-testid="agent-send-btn"
                            onClick={() => ask(input)}
                            disabled={busy || !input.trim()}
                            className="bg-[var(--brand-amber)] text-black px-3 font-mono-fin uppercase text-[11px] tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-1"
                        >
                            <Send size={12} /> {busy ? "…" : "Send"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AIAgents() {
    const [agents, setAgents] = useState([]);
    const [digests, setDigests] = useState({});
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    const loadAll = async () => {
        setLoading(true);
        const r = await api.get("/agents");
        setAgents(r.data.agents || []);
        // fetch approvals only (cheap, no LLM) for the hub card counts
        const map = {};
        await Promise.all((r.data.agents || []).map(async (a) => {
            try {
                const ap = await api.get(`/agents/${a.key}/approvals`);
                map[a.key] = { signals: ap.data.approvals || [] };
            } catch (e) {
                map[a.key] = { signals: [] };
            }
        }));
        setDigests(map);
        setLoading(false);
    };

    useEffect(() => { loadAll(); }, []);

    if (active) {
        return <AgentPanel agent={active} onBack={() => setActive(null)} />;
    }

    return (
        <div className="space-y-5">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// AUTONOMOUS AGENTS</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <Bot className="text-[var(--brand-amber)]" size={26} />
                    AI Agents Hub
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Every agent watches its domain 24/7 · surfaces signals · asks only for your OK on the ones that matter
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="agents-grid">
                {agents.map((a) => (
                    <AgentCard
                        key={a.key}
                        agent={a}
                        digest={digests[a.key]}
                        loading={loading}
                        onOpen={() => setActive(a)}
                    />
                ))}
            </div>

            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] pt-4 border-t border-panel">
                Powered by Claude Sonnet 4.5 · Live snapshot pulled from MongoDB + seed data · No manual configuration needed
            </div>
        </div>
    );
}
